import { Link } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { ArrowRight, ArrowUpRight, Lock, Scale, Globe, AlertTriangle, Clock, CheckCircle2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/SEOHead';
import { CornerAccents } from '@/components/CornerAccents';
import { Reveal } from '@/components/motion/Reveal';
import { SpotlightCard } from '@/components/motion/SpotlightCard';
import { CountUp } from '@/components/motion/CountUp';
import { INSIGHTS } from '@/data/insights';

const HEADING_FONT = { fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" };
const BODY_FONT = { fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" };
const ITEM_V = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] } },
};

function RiskFlagsColumn() {
  const { t, lang } = useLanguage();
  const allRows = useMemo(
    () => [
      { icon: AlertTriangle, color: '#F59E0B', text: t('home.sim_r1'), tag: t('home.sim_r1_tag') },
      { icon: Clock, color: '#6366F1', text: t('home.sim_r2'), tag: t('home.sim_r2_tag') },
      { icon: CheckCircle2, color: '#10B981', text: t('home.sim_r3'), tag: t('home.sim_r3_tag') },
      { icon: AlertTriangle, color: '#EF4444', text: t('home.sim_r6'), tag: t('home.sim_r6_tag') },
      { icon: CheckCircle2, color: '#10B981', text: t('home.sim_r4'), tag: t('home.sim_r4_tag') },
      { icon: AlertTriangle, color: '#F59E0B', text: t('home.sim_r5'), tag: t('home.sim_r5_tag') },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang]
  );
  const [head, setHead] = useState(3);
  useEffect(() => {
    const id = setInterval(() => setHead((h) => (h + 1) % allRows.length), 2400);
    return () => clearInterval(id);
  }, [allRows.length]);
  const visible = [3, 2, 1, 0].map((off) => (head - off + allRows.length) % allRows.length);
  return (
    <div className="space-y-2 min-h-[190px]">
      <AnimatePresence mode="popLayout" initial={false}>
        {visible.map((idx) => {
          const r = allRows[idx];
          return (
            <motion.div
              key={`${idx}-${r.text}`}
              layout
              initial={{ opacity: 0, x: 20, filter: 'blur(3px)' }}
              animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="flex items-center justify-between rounded-lg bg-white/[0.03] border border-slate-800/60 px-3 py-2.5"
            >
              <span className="flex items-center gap-2.5 text-xs text-[#F8FAFC] truncate" style={BODY_FONT}>
                <r.icon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: r.color }} />
                <span className="truncate">{r.text}</span>
              </span>
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 420, damping: 18, delay: 0.3 }}
                className="text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ml-2"
                style={{ color: r.color, backgroundColor: `${r.color}1a`, border: `1px solid ${r.color}40` }}
              >
                {r.tag}
              </motion.span>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

const STREAM_FILES = [
  { name: 'subcontract_v3.pdf', pct: 78, active: true },
  { name: 'LC_MT700_4471.txt', pct: 100, active: false },
  { name: 'main_contract.docx', pct: 100, active: false },
  { name: 'variation_order_18.pdf', pct: 12, active: true },
];

function StreamColumn() {
  return (
    <div className="space-y-3">
      {STREAM_FILES.map((f, i) => (
        <div key={f.name} className="rounded-lg bg-white/[0.03] border border-slate-800/60 px-3 py-2.5">
          <div className="flex items-center justify-between mb-2">
            <span className="flex items-center gap-2 font-mono text-[10px] text-[#94A3B8] truncate">
              <FileText className="h-3 w-3 text-[#818CF8] flex-shrink-0" />
              {f.name}
            </span>
            {f.pct === 100 ? (
              <CheckCircle2 className="h-3 w-3 text-[#10B981] flex-shrink-0" />
            ) : (
              <span className="font-mono text-[9px] text-[#64748B]">{f.pct}%</span>
            )}
          </div>
          <div className="h-1 rounded-full bg-white/[0.05] overflow-hidden">
            {f.active ? (
              <motion.span
                className="block h-full rounded-full bg-gradient-to-r from-[#6366F1] to-[#818CF8]"
                animate={{ width: [`${Math.max(f.pct - 25, 5)}%`, `${f.pct}%`] }}
                transition={{ duration: 2.4, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay: i * 0.4 }}
              />
            ) : (
              <span className="block h-full rounded-full bg-[#10B981]/60" style={{ width: `${f.pct}%` }} />
            )}
          </div>
        </div>
      ))}
      <p className="font-mono text-[9px] tracking-widest text-[#64748B] pt-1">
        4 DOCS · <span className="text-[#10B981]">● IN-MEMORY PROCESSING</span>
      </p>
    </div>
  );
}

function ScoreDial() {
  const R = 52;
  const C = 2 * Math.PI * R;
  const score = 94;
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="relative w-36 h-36">
        <svg viewBox="0 0 128 128" className="w-full h-full -rotate-90">
          <circle cx="64" cy="64" r={R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
          <motion.circle
            cx="64" cy="64" r={R} fill="none" stroke="url(#dialGrad)" strokeWidth="8" strokeLinecap="round"
            strokeDasharray={C}
            initial={{ strokeDashoffset: C }}
            whileInView={{ strokeDashoffset: C * (1 - score / 100) }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, ease: [0.21, 0.47, 0.32, 0.98], delay: 0.4 }}
            style={{ filter: 'drop-shadow(0 0 8px rgba(99,102,241,0.6))' }}
          />
          <defs>
            <linearGradient id="dialGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#6366F1" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-[#F8FAFC] tracking-tight" style={HEADING_FONT}>{score}</span>
          <span className="font-mono text-[9px] tracking-widest text-[#64748B]">/ 100</span>
        </div>
      </div>
      <span className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 font-mono text-[9px] font-bold tracking-widest text-[#10B981]">
        <span className="w-1 h-1 rounded-full bg-[#10B981]" style={{ animation: 'pulse-dot 2s ease-in-out infinite' }} />
        UCP 600 · US SUBCONTRACTS
      </span>
    </div>
  );
}

const MOCK_CAPTION = { en: 'Illustrative example', 'zh-TW': '示意範例', 'zh-CN': '示意示例' };

function CommandCenter() {
  const { t, lang } = useLanguage();
  const cols = [
    { key: 'sim_stream', body: <StreamColumn /> },
    { key: 'sim_flags', body: <RiskFlagsColumn /> },
    { key: 'sim_score', body: <ScoreDial /> },
  ];
  return (
    <div className="relative max-w-[1200px] mx-auto w-full">
      <div
        aria-hidden
        className="absolute -inset-x-24 -top-20 -bottom-24 pointer-events-none"
        style={{ background: 'radial-gradient(720px circle at 50% 40%, rgba(99,102,241,0.16), transparent 60%)' }}
      />
      <div
        data-testid="hero-command-center"
        className="relative rounded-2xl bg-[#0E131F]/85 backdrop-blur-xl border border-slate-800/60 shadow-[0_32px_80px_rgba(0,0,0,0.55)] overflow-hidden"
      >
        <div aria-hidden className="absolute top-0 inset-x-8 h-px bg-gradient-to-r from-transparent via-[#818CF8]/70 to-transparent" />
        <CornerAccents />
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800/60 bg-white/[0.02] backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="relative w-5 h-5" aria-hidden>
              <span className="absolute inset-0 rounded-full border border-[#6366F1]/40" />
              <span
                className="absolute inset-0 rounded-full"
                style={{ background: 'conic-gradient(from 0deg, rgba(99,102,241,0.9), transparent 90deg)', animation: 'radar-sweep 2.4s linear infinite' }}
              />
              <span className="absolute inset-[38%] rounded-full bg-[#6366F1]" />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
            </div>
          </div>
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#64748B]">vowpace.com / sample_audit</span>
          <span className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[9px] font-bold tracking-widest text-[#10B981]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" style={{ animation: 'pulse-dot 2s ease-in-out infinite' }} />
            ONLINE
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-800/60">
          {cols.map((col) => (
            <div key={col.key} className="p-5 md:p-6">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B] mb-4">{t(`home.${col.key}`)}</p>
              {col.body}
            </div>
          ))}
        </div>
        <div className="px-5 py-2.5 border-t border-slate-800/60 bg-white/[0.02]">
          <p className="flex items-center gap-2 text-[10px] font-mono text-[#10B981]/90">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" style={{ animation: 'pulse-dot 2s ease-in-out infinite' }} />
            {t('home.sim_done')}
          </p>
        </div>
      </div>
      <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-[#64748B]">{MOCK_CAPTION[lang] || MOCK_CAPTION.en}</p>
    </div>
  );
}

function HeroSection() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  return (
    <section ref={ref} data-testid="hero-section" className="relative flex flex-col justify-center bg-[#080B10] overflow-hidden pt-32 md:pt-40 pb-20">
      <div className="absolute inset-0">
        <motion.img src="/img/hero-architecture.jpg" alt="" aria-hidden style={{ y: bgY }} className="w-full h-[118%] object-cover" />
        <div className="absolute inset-0 bg-[#080B10]/[0.9]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080B10]/70 via-transparent to-[#080B10]" />
      </div>
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '72px 72px',
        }}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <motion.div
          className="text-center flex flex-col items-center"
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}
        >
          <motion.span
            variants={ITEM_V}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-slate-800/60 font-mono text-[10px] md:text-xs font-bold tracking-[0.25em] text-[#818CF8] mb-8"
          >
            DZENT LIMITED | VERTICAL B2B AI EXPERT
          </motion.span>
          <motion.h1
            variants={ITEM_V}
            data-testid="hero-title"
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold text-[#F8FAFC] leading-[1.1] mb-7 max-w-4xl"
            style={HEADING_FONT}
          >
            {t('home.hero_title_l1')}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6366F1] to-[#F59E0B]">
              {t('home.hero_title_l2')}
            </span>
          </motion.h1>
          <motion.p variants={ITEM_V} data-testid="hero-subtitle" className="text-base md:text-lg text-[#94A3B8] leading-relaxed max-w-3xl mb-10" style={BODY_FONT}>
            <span className="block [text-wrap:balance]">{t('home.hero_subtitle_l1')}</span>
            <span className="block [text-wrap:balance] mt-1">{t('home.hero_subtitle_l2')}</span>
          </motion.p>
          <motion.div variants={ITEM_V} className="flex flex-wrap items-center justify-center gap-4 mb-16">
            <Link to="/products">
              <Button
                data-testid="hero-cta-button"
                className="h-12 px-8 rounded-full bg-[#6366F1] text-white font-bold text-sm hover:bg-white hover:text-[#080B10] transition-all shadow-lg shadow-[#6366F1]/30"
              >
                {t('home.hero_cta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                data-testid="hero-cta-secondary"
                variant="outline"
                className="h-12 px-8 rounded-full bg-transparent border-white/20 text-[#F8FAFC] font-semibold text-sm hover:bg-white/10 hover:text-white transition-all"
              >
                {t('home.hero_cta2')}
              </Button>
            </Link>
          </motion.div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 48, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
        >
          <CommandCenter />
        </motion.div>
      </div>
    </section>
  );
}

function MetricsBar() {
  const { t } = useLanguage();
  const metrics = ['m1', 'm2', 'm3', 'm4'];
  return (
    <section data-testid="metrics-bar" className="bg-[#080B10] border-y border-slate-800/60">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4">
        {metrics.map((m, i) => (
          <div
            key={m}
            data-testid={`home-metric-${i}`}
            className={`py-10 md:py-12 text-center ${i > 0 ? 'lg:border-l border-slate-800/60' : ''} ${i % 2 === 1 ? 'border-l border-slate-800/60 lg:border-l' : ''}`}
          >
            <Reveal delay={i * 0.08} y={14}>
              <p className="text-3xl md:text-4xl font-bold text-[#818CF8] tracking-tight mb-2" style={HEADING_FONT}>
                <CountUp value={t(`home.${m}_v`)} />
              </p>
              <p className="text-xs md:text-sm text-[#64748B]" style={BODY_FONT}>{t(`home.${m}_l`)}</p>
            </Reveal>
          </div>
        ))}
      </div>
    </section>
  );
}

function FlagshipCard({ id, name, tagline, desc, image, imgClass, logo, accent, overview, launch, tags }) {
  const { t } = useLanguage();

  return (
    <SpotlightCard glow={accent} className="rounded-3xl">
    <div
      data-testid={`home-flagship-${id}`}
      className="group grid grid-cols-1 lg:grid-cols-2 rounded-3xl bg-[#0E131F] border border-slate-800/60 overflow-hidden hover:border-[#6366F1]/30 transition-colors duration-300"
    >
      <div className="relative aspect-[16/10] lg:aspect-auto lg:min-h-[340px] bg-[#0A0E16] overflow-hidden">
        <img src={image} alt={name} className={`w-full h-full object-cover ${imgClass} group-hover:scale-[1.03] transition-transform duration-700`} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E131F]/70 via-transparent to-transparent lg:bg-gradient-to-r" />
        {logo && (
          <img src={logo} alt={`${name} logo`} className="absolute bottom-5 left-5 w-12 h-auto" style={{ filter: 'drop-shadow(0 0 10px rgba(129,140,248,0.7))' }} />
        )}
      </div>
      <div className="p-8 md:p-12 flex flex-col justify-center">
        <div className="flex flex-wrap gap-2 mb-5">
          {tags.map((x) => (
            <span key={x} className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide" style={{ color: accent, backgroundColor: `${accent}14`, border: `1px solid ${accent}35` }}>
              {x}
            </span>
          ))}
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight mb-2" style={HEADING_FONT}>{name}</h3>
        <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B] mb-4" style={BODY_FONT}>{tagline}</p>
        <p className="text-sm md:text-base text-[#94A3B8] leading-relaxed mb-8 max-w-[58ch]" style={BODY_FONT}>{desc}</p>
        <div className="flex flex-wrap gap-3">
          <Link to={overview}>
            <Button
              data-testid={`home-flagship-cta-${id}`}
              className="h-11 px-6 rounded-full text-sm font-bold text-white hover:brightness-110 transition-all"
              style={{ backgroundColor: accent, boxShadow: `0 6px 22px ${accent}40` }}
            >
              {t('products.learn_more')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <a href={launch} target="_blank" rel="noopener noreferrer">
            <Button
              data-testid={`home-flagship-launch-${id}`}
              variant="outline"
              className="h-11 px-6 rounded-full bg-transparent border-white/20 text-[#F8FAFC] text-sm font-semibold hover:bg-white/10 hover:text-white transition-all"
            >
              {t('products.launch_app')}
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </div>
    </div>
    </SpotlightCard>
  );
}

function FlagshipSection() {
  const { t } = useLanguage();
  return (
    <section data-testid="flagship-section" className="py-24 md:py-32 bg-[#080B10]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="mb-14">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#818CF8] mb-4">{t('home.flagship_label')}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F8FAFC] max-w-2xl" style={HEADING_FONT}>
            {t('home.flagship_title')}
          </h2>
        </Reveal>
        <div className="space-y-8">
          <Reveal>
          <FlagshipCard
            id="vowpace"
            name="Vowpace AI"
            tagline={t('products.vw_tagline')}
            desc={t('products.vw_desc')}
            image="/press-kit/vowpace-og.jpg"
            imgClass="object-left"
            accent="#6366F1"
            overview="/products/vowpace-ai"
            launch="https://vowpace.com"
            tags={['B2B Enterprise', 'Construction AI']}
          />
          </Reveal>
          <Reveal delay={0.1}>
          <FlagshipCard
            id="lcshield"
            name="LC Shield"
            tagline={t('products.lc_tagline')}
            desc={t('products.lc_desc')}
            image="/press-kit/lcshield-og.png"
            imgClass=""
            logo="/press-kit/lcshield-logo.png"
            accent="#F59E0B"
            overview="/products/lc-shield"
            launch="https://getlcshield.com"
            tags={['B2B FinTech', 'International Trade']}
          />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  const { t } = useLanguage();
  const pillars = [
    { icon: Lock, key: 'pillar1', accent: '#10B981' },
    { icon: Scale, key: 'pillar2', accent: '#6366F1' },
    { icon: Globe, key: 'pillar3', accent: '#F59E0B' },
  ];
  return (
    <section data-testid="trust-section" className="py-24 md:py-32 bg-[#080B10] relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(600px circle at 50% 0%, rgba(99,102,241,0.07), transparent 60%)' }}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="text-center mb-16">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#818CF8] mb-4">{t('home.trust_label')}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F8FAFC]" style={HEADING_FONT}>
            {t('home.trust_title')}
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {pillars.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.12}>
            <SpotlightCard glow={p.accent} className="rounded-3xl h-full">
            <div
              data-testid={`trust-pillar-${i}`}
              className="h-full rounded-3xl bg-[#0E131F] border border-slate-800/60 p-8 md:p-10 hover:-translate-y-1 transition-transform duration-300"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: `${p.accent}14`, border: `1px solid ${p.accent}30` }}>
                <p.icon className="h-6 w-6" style={{ color: p.accent }} />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-[#F8FAFC] mb-3" style={HEADING_FONT}>{t(`home.${p.key}_t`)}</h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed" style={BODY_FONT}>{t(`home.${p.key}_d`)}</p>
            </div>
            </SpotlightCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function InsightsSection() {
  const { t, lang } = useLanguage();
  return (
    <section data-testid="insights-section" className="py-24 md:py-32 bg-[#080B10] border-t border-slate-800/60">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="flex items-end justify-between mb-12">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#818CF8]">{t('home.insights_label')}</p>
          <Link
            to="/insights"
            data-testid="home-insights-view-all"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#94A3B8] hover:text-[#6366F1] transition-colors"
          >
            {t('home.insights_view_all')}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {INSIGHTS.map((insight, idx) => {
            const meta = insight.meta[lang] || insight.meta.en;
            return (
              <Reveal key={insight.id} delay={idx * 0.1} className="h-full">
              <Link
                to={insight.path}
                data-testid={`home-insight-card-${insight.id}`}
                className="group flex flex-col h-full rounded-2xl md:rounded-3xl bg-[#0E131F] border border-slate-800/60 p-8 hover:border-[#6366F1]/30 hover:-translate-y-1 transition-all duration-300"
              >
                <span
                  className="self-start inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wide mb-5"
                  style={{ backgroundColor: `${insight.accent}14`, border: `1px solid ${insight.accent}40`, color: insight.accent }}
                >
                  {meta.category}
                </span>
                <h3 className="text-lg md:text-xl tracking-tight font-bold text-[#F8FAFC] leading-snug mb-3 group-hover:text-[#6366F1] transition-colors" style={HEADING_FONT}>
                  {meta.title}
                </h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed mb-6 flex-1" style={BODY_FONT}>{meta.desc}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#64748B]">{meta.date}</span>
                  <ArrowRight className="h-4 w-4 text-[#818CF8] group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CTABand() {
  const { t } = useLanguage();
  return (
    <section data-testid="home-cta-band" className="py-20 md:py-28 bg-[#080B10]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
        <div className="relative rounded-3xl border border-slate-800/60 px-8 py-14 md:px-16 md:py-20 text-center overflow-hidden bg-[#0E131F]">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(480px circle at 20% 30%, rgba(99,102,241,0.14), transparent 60%), radial-gradient(520px circle at 80% 70%, rgba(99,102,241,0.10), transparent 60%)',
            }}
          />
          <div className="relative z-10">
            <h2 className="text-2xl md:text-3xl lg:text-4xl tracking-tight font-bold text-[#F8FAFC] mb-4" style={HEADING_FONT}>
              {t('home.cta_band_t')}
            </h2>
            <p className="text-sm md:text-base text-[#94A3B8] mb-8" style={BODY_FONT}>{t('home.cta_band_d')}</p>
            <Link to="/contact">
              <Button
                data-testid="home-cta-band-button"
                className="h-12 px-8 rounded-full bg-[#6366F1] text-white font-bold text-sm hover:bg-white hover:text-[#080B10] transition-all shadow-lg shadow-[#6366F1]/25"
              >
                {t('home.cta_band_btn')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div data-testid="home-page" className="bg-[#080B10]">
      <SEOHead path="/" />
      <HeroSection />
      <MetricsBar />
      <FlagshipSection />
      <TrustSection />
      <InsightsSection />
      <CTABand />
    </div>
  );
}
